import { useBootstrapStore } from '~/stores/bootstrap';
import { isOfflineBoot } from '~/services/storage/offlineBuckets';

// Routes allowed while offline. Anything outside this list redirects to /offline-blocked.
// Goal: keep selling possible end-to-end through to close-of-day; everything else
// (settings/menu/reports/admin) requires network.
const OFFLINE_ALLOWED_EXACT = new Set<string>([
  '/',               // root — bootstrap navigates to /v2 from here
  '/home',           // alias to root
  '/index',
  '/v2',
  '/v2/',
  '/v2/pos',
  '/runMode',
  '/login',
  '/offline-blocked',
  '/pos',
  '/pos-cashier-changed',
  '/openShop',
  '/add-day',
]);

const OFFLINE_ALLOWED_PREFIX = [
  '/v2/payment',  // payment flow during sale
];

function isAllowedOffline(path: string): boolean {
  if (OFFLINE_ALLOWED_EXACT.has(path)) return true;
  return OFFLINE_ALLOWED_PREFIX.some((p) => path === p || path.startsWith(p + '/'));
}

function isCurrentlyOffline(bootstrapStore: ReturnType<typeof useBootstrapStore>): boolean {
  // Prefer the RTDB-cached offline signal (set by detectOfflineBoot at
  // bootstrap). It's much more reliable than navigator.onLine, which lies
  // in production (returns true even when the device can't reach cloud).
  // Falls back to navigator.onLine + bootstrapStore.isOffline if the cache
  // hasn't been populated yet (e.g., very first middleware tick before
  // bootstrap runs detectOfflineBoot).
  if (isOfflineBoot()) return true;
  const navOff = typeof navigator !== 'undefined' && navigator.onLine === false;
  return navOff || bootstrapStore.isOffline === true;
}

export default defineNuxtRouteMiddleware((to) => {
  const bootstrapStore = useBootstrapStore();

  if (!isCurrentlyOffline(bootstrapStore)) return;
  if (isAllowedOffline(to.path)) return;
  if (to.path.startsWith('/v2-owner-report-mobile')) return; // owner mobile is its own world

  return navigateTo({ path: '/offline-blocked', query: { from: to.fullPath } });
});
